/**
 * Cart cross-sell carousel: complementary → related → fallback,
 * 2 products per slide, autoplay, gift-bag style toggles.
 */
if (!customElements.get('cart-cross-sell')) {
  class CartCrossSell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.ui = this.querySelector('[data-cart-cross-sell-ui]');
      this.track = this.querySelector('[data-cart-cross-sell-track]');
      this.dots = this.querySelector('[data-cart-cross-sell-dots]');
      this.fallbackTemplate = this.querySelector('[data-cart-cross-sell-fallback]');
      this.currentSlide = 0;
      this.slideCount = 0;
      this.timer = null;
      this.isInteracting = false;
      this.cartUpdateUnsubscriber = undefined;

      this.onToggleChange = this.onToggleChange.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onSwipePointerDown = this.onSwipePointerDown.bind(this);
      this.onSwipePointerMove = this.onSwipePointerMove.bind(this);
      this.onSwipePointerUp = this.onSwipePointerUp.bind(this);

      this.viewport = this.querySelector('[data-cart-cross-sell-viewport]');
      this.drag = null;

      this.addEventListener('change', this.onToggleChange);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.viewport?.addEventListener('pointerdown', this.onSwipePointerDown);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }

      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.teardownSwipeListeners();
      this.removeEventListener('change', this.onToggleChange);
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.viewport?.removeEventListener('pointerdown', this.onSwipePointerDown);
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = undefined;
      }
    }

    get productId() {
      return this.dataset.productId || '';
    }

    get limit() {
      return Math.max(1, Number(this.dataset.limit || 8));
    }

    get perSlide() {
      return Math.max(1, Number(this.dataset.perSlide || 2));
    }

    get interval() {
      return Math.max(2500, Number(this.dataset.interval || 5000));
    }

    getSections() {
      const cartDrawer = document.querySelector('cart-drawer');
      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');
      const sections = ['cart-icon-bubble'];

      if (cartDrawer) sections.push('cart-drawer');
      if (mainCartItems) sections.push(mainCartItems.dataset.id);
      if (mainCartFooter) sections.push(mainCartFooter.dataset.id);

      return sections;
    }

    async loadRecommendations() {
      if (!this.productId || !this.track) {
        this.hide();
        return;
      }

      const seen = new Set();
      const items = [];

      const collect = (nodes) => {
        nodes.forEach((node) => {
          const id = node.dataset.productId;
          if (!id || seen.has(id) || items.length >= this.limit) return;
          seen.add(id);
          items.push(node);
        });
      };

      try {
        if (this.dataset.urlComplementary) {
          collect(await this.fetchItems(this.dataset.urlComplementary));
        }
        if (items.length < this.limit && this.dataset.urlRelated) {
          collect(await this.fetchItems(this.dataset.urlRelated));
        }
        if (items.length < this.limit) {
          collect(this.getFallbackItems());
        }
      } catch (error) {
        console.error(error);
        collect(this.getFallbackItems());
      }

      if (!items.length) {
        this.hide();
        return;
      }

      this.buildCarousel(items);
      this.hidden = false;
    }

    async fetchItems(baseUrl) {
      const url = `${baseUrl}&product_id=${this.productId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Cross-sell request failed: ${response.status}`);
      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const section = html.querySelector('.shopify-section') || html;
      if (section.querySelector('[data-cart-cross-sell-empty]')) return [];
      return Array.from(section.querySelectorAll('[data-cart-cross-sell-item]'));
    }

    getFallbackItems() {
      if (!this.fallbackTemplate) return [];
      const clone = this.fallbackTemplate.content.cloneNode(true);
      return Array.from(clone.querySelectorAll('[data-cart-cross-sell-item]'));
    }

    buildCarousel(items) {
      this.stopAutoplay();
      this.track.innerHTML = '';
      if (this.dots) this.dots.innerHTML = '';

      const slides = [];
      for (let i = 0; i < items.length; i += this.perSlide) {
        const slide = document.createElement('ul');
        slide.className = 'cart-cross-sell__slide list-unstyled';
        slide.setAttribute('role', 'list');
        slide.setAttribute('aria-hidden', slides.length === 0 ? 'false' : 'true');
        items.slice(i, i + this.perSlide).forEach((item) => slide.appendChild(item));
        this.track.appendChild(slide);
        slides.push(slide);
      }

      this.slideCount = slides.length;
      this.currentSlide = 0;
      this.track.style.transform = 'translateX(0%)';

      if (this.dots) {
        if (this.slideCount > 1) {
          this.dots.hidden = false;
          for (let i = 0; i < this.slideCount; i += 1) {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cart-cross-sell__dot';
            dot.dataset.slideIndex = String(i);
            dot.setAttribute('aria-label', `Slide ${i + 1}`);
            if (i === 0) dot.classList.add('is-active');
            dot.addEventListener('click', this.onDotClick);
            this.dots.appendChild(dot);
          }
        } else {
          this.dots.hidden = true;
        }
      }

      this.startAutoplay();
    }

    goToSlide(index) {
      if (this.slideCount < 2) return;
      this.currentSlide = ((index % this.slideCount) + this.slideCount) % this.slideCount;
      this.track.style.transition = '';
      this.track.style.transform = `translateX(-${this.currentSlide * 100}%)`;

      this.track.querySelectorAll('.cart-cross-sell__slide').forEach((slide, i) => {
        slide.setAttribute('aria-hidden', i === this.currentSlide ? 'false' : 'true');
      });

      this.dots?.querySelectorAll('.cart-cross-sell__dot').forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.currentSlide);
      });
    }

    onSwipePointerDown(event) {
      if (this.slideCount < 2 || !this.track || !this.viewport) return;
      if (event.button !== 0 && event.pointerType === 'mouse') return;
      // Keep toggles / dots interactive; allow swipe from product rows.
      if (event.target.closest('.cart-cross-sell__switch, .cart-cross-sell__dot, button')) return;

      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        axis: null,
        dragging: false,
      };

      this.isInteracting = true;
      this.stopAutoplay();

      this.viewport.addEventListener('pointermove', this.onSwipePointerMove);
      this.viewport.addEventListener('pointerup', this.onSwipePointerUp);
      this.viewport.addEventListener('pointercancel', this.onSwipePointerUp);

      try {
        this.viewport.setPointerCapture(event.pointerId);
      } catch (error) {
        // Older browsers may reject capture; move/up still work on the element.
      }
    }

    onSwipePointerMove(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const dx = event.clientX - this.drag.startX;
      const dy = event.clientY - this.drag.startY;

      if (!this.drag.axis) {
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        // Prefer vertical page scroll when the gesture is mostly vertical.
        this.drag.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x';
        if (this.drag.axis === 'y') {
          this.finishSwipe(false);
          return;
        }
        this.drag.dragging = true;
        this.track.classList.add('is-dragging');
        this.track.style.transition = 'none';
        this.viewport.classList.add('is-dragging');
      }

      if (this.drag.axis !== 'x') return;

      event.preventDefault();
      this.drag.deltaX = dx;
      const width = this.viewport.offsetWidth || 1;
      const basePct = -this.currentSlide * 100;
      const dragPct = (dx / width) * 100;
      this.track.style.transform = `translateX(${basePct + dragPct}%)`;
    }

    onSwipePointerUp(event) {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      const wasDragging = this.drag.dragging;
      const deltaX = this.drag.deltaX;
      this.finishSwipe(wasDragging, deltaX);
    }

    finishSwipe(wasDragging, deltaX = 0) {
      const drag = this.drag;
      this.drag = null;
      this.teardownSwipeListeners(drag?.pointerId);

      this.track?.classList.remove('is-dragging');
      this.viewport?.classList.remove('is-dragging');
      if (this.track) this.track.style.transition = '';

      if (wasDragging && this.slideCount > 1) {
        const width = this.viewport?.offsetWidth || 1;
        const threshold = Math.min(64, width * 0.2);

        if (deltaX <= -threshold) {
          this.goToSlide(this.currentSlide + 1);
        } else if (deltaX >= threshold) {
          this.goToSlide(this.currentSlide - 1);
        } else {
          this.goToSlide(this.currentSlide);
        }

        // Avoid accidental product-link navigation after a horizontal drag.
        if (Math.abs(deltaX) > 10) {
          const suppressClick = (clickEvent) => {
            clickEvent.preventDefault();
            clickEvent.stopPropagation();
            this.viewport?.removeEventListener('click', suppressClick, true);
          };
          this.viewport?.addEventListener('click', suppressClick, true);
        }
      }

      this.isInteracting = false;
      this.startAutoplay();
    }

    teardownSwipeListeners(pointerId) {
      this.viewport?.removeEventListener('pointermove', this.onSwipePointerMove);
      this.viewport?.removeEventListener('pointerup', this.onSwipePointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onSwipePointerUp);
      if (pointerId != null && this.viewport?.hasPointerCapture?.(pointerId)) {
        try {
          this.viewport.releasePointerCapture(pointerId);
        } catch (error) {
          // no-op
        }
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (this.slideCount < 2 || this.isInteracting) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (document.hidden) return;

      this.timer = window.setInterval(() => {
        this.goToSlide(this.currentSlide + 1);
      }, this.interval);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    onPointerEnter() {
      this.isInteracting = true;
      this.stopAutoplay();
    }

    onPointerLeave() {
      this.isInteracting = false;
      this.startAutoplay();
    }

    onVisibilityChange() {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    }

    onDotClick(event) {
      const index = Number(event.currentTarget.dataset.slideIndex);
      if (Number.isNaN(index)) return;
      this.goToSlide(index);
      this.stopAutoplay();
      this.startAutoplay();
    }

    hide() {
      this.hidden = true;
      this.stopAutoplay();
    }

    onCartUpdate(event) {
      const cartData = event?.cartData;
      if (!cartData) return;

      if (Number(cartData.item_count) === 0) {
        this.hide();
        return;
      }

      this.hidden = false;
      this.syncFromCart(cartData);
    }

    syncFromCart(cartData) {
      const items = Array.isArray(cartData?.items) ? cartData.items : [];
      const byVariant = new Map();

      items.forEach((cartItem) => {
        if (!cartItem?.variant_id || Number(cartItem.quantity) < 1) return;
        byVariant.set(String(cartItem.variant_id), cartItem);
      });

      this.querySelectorAll('[data-cart-cross-sell-item]').forEach((item) => {
        const variantId = String(item.dataset.variantId || '');
        const input = item.querySelector('[data-cart-cross-sell-toggle]');
        if (!variantId || !input || input.disabled) return;

        const cartItem = byVariant.get(variantId);
        const inCart = Boolean(cartItem);

        input.checked = inCart;
        item.classList.toggle('is-active', inCart);
        item.dataset.lineKey = cartItem?.key || '';
        item.dataset.quantity = inCart ? String(cartItem.quantity) : '0';
      });
    }

    onToggleChange(event) {
      const input = event.target.closest('[data-cart-cross-sell-toggle]');
      if (!input || !this.contains(input)) return;
      const item = input.closest('[data-cart-cross-sell-item]');
      if (!item) return;

      if (input.checked) this.addItem(item, input);
      else this.removeItem(item, input);
    }

    setItemLoading(item, input, isLoading) {
      item.classList.toggle('is-loading', isLoading);
      input.disabled = isLoading;
    }

    revertToggle(input, checked) {
      input.checked = checked;
      input.closest('[data-cart-cross-sell-item]')?.classList.toggle('is-active', checked);
    }

    async addItem(item, input) {
      const variantId = item.dataset.variantId;
      if (!variantId) return;

      this.setItemLoading(item, input, true);

      try {
        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: this.getSections(),
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();

        if (parsedState.status) {
          this.revertToggle(input, false);
          console.error(parsedState.description || parsedState.message);
          return;
        }

        const added = parsedState.items?.find((cartItem) => String(cartItem.variant_id) === String(variantId));
        if (added?.key) item.dataset.lineKey = added.key;
        item.dataset.quantity = '1';
        item.classList.add('is-active');

        this.refreshCart(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-cross-sell',
          productVariantId: variantId,
          cartData: parsedState,
        });
      } catch (error) {
        console.error(error);
        this.revertToggle(input, false);
      } finally {
        this.setItemLoading(item, input, false);
      }
    }

    async removeItem(item, input) {
      const variantId = item.dataset.variantId;
      const lineKey = item.dataset.lineKey;
      if (!variantId && !lineKey) return;

      this.setItemLoading(item, input, true);

      try {
        const response = await fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: lineKey || variantId,
            quantity: 0,
            sections: this.getSections(),
            sections_url: window.location.pathname,
          }),
        });
        const parsedState = await response.json();

        if (parsedState.errors) {
          this.revertToggle(input, true);
          console.error(parsedState.errors);
          return;
        }

        item.dataset.lineKey = '';
        item.dataset.quantity = '0';
        item.classList.remove('is-active');

        this.refreshCart(parsedState);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-cross-sell',
          productVariantId: variantId,
          cartData: parsedState,
        });
      } catch (error) {
        console.error(error);
        this.revertToggle(input, true);
      } finally {
        this.setItemLoading(item, input, false);
      }
    }

    getSectionInnerHTML(html, selector) {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
    }

    refreshCart(parsedState) {
      if (!parsedState?.sections) {
        window.location.href = window.routes.cart_url;
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && parsedState.sections['cart-drawer']) {
        const sectionElement = document.getElementById('CartDrawer');
        if (sectionElement) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-drawer'], '#CartDrawer');
          cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
          sectionElement
            .querySelector('#CartDrawer-Overlay')
            ?.addEventListener('click', cartDrawer.close.bind(cartDrawer));
          if (window.SSPaymentIcons && typeof window.SSPaymentIcons.mount === 'function') {
            window.SSPaymentIcons.mount();
          }
        }
        return;
      }

      if (parsedState.sections['cart-icon-bubble']) {
        const iconBubble = document.getElementById('cart-icon-bubble');
        if (iconBubble) {
          iconBubble.innerHTML = this.getSectionInnerHTML(
            parsedState.sections['cart-icon-bubble'],
            '.shopify-section'
          );
        }
      }

      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems && parsedState.sections[mainCartItems.dataset.id]) {
        const contents = mainCartItems.querySelector('.js-contents');
        if (contents) {
          contents.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[mainCartItems.dataset.id],
            '.js-contents'
          );
        }
        document.querySelector('cart-items')?.classList.toggle('is-empty', parsedState.item_count === 0);
      }

      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter && parsedState.sections[mainCartFooter.dataset.id]) {
        const footerSectionHtml = parsedState.sections[mainCartFooter.dataset.id];
        const contents = mainCartFooter.querySelector('.js-contents');
        if (contents) {
          contents.innerHTML = this.getSectionInnerHTML(footerSectionHtml, '.js-contents');
        }
        const giftBag = mainCartFooter.querySelector('#CartGiftBag');
        if (giftBag) {
          giftBag.innerHTML = this.getSectionInnerHTML(footerSectionHtml, '#CartGiftBag');
        }
        mainCartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      }
    }
  }

  customElements.define('cart-cross-sell', CartCrossSell);
}
